/**
 * EntityCardGrid — Responsive card grid for entity data
 *
 * Generic wrapper around SimpleGrid + EntityCard.
 * Works for BOTH FERP primitives and subgraph entities via the fieldAdapter pattern.
 *
 * Also exports field adapter factories:
 * - primitiveFieldAdapter: PrimitiveItem → EntityFieldConfig
 * - subgraphFieldAdapter: SubgraphEntityType → (row) → EntityFieldConfig
 *
 * DRR-288: Terminology injection via optional getLabel parameter.
 * Labels like 'Subject', 'Assigned' resolve via ViewContext terminology.
 */

import { SimpleGrid, Text, Center, ScrollArea } from '@mantine/core';
import { EntityCard, type EntityFieldConfig } from './EntityCard';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import {
  getPrimitiveMetadata,
  type PrimitiveType,
} from '@/config/primitive-metadata';
import type { SubgraphEntityType } from '@/hooks/useTenantSubgraphConfig';

// ============================================================================
// GRID COMPONENT
// ============================================================================

interface EntityCardGridProps<T> {
  items: T[];
  fieldAdapter: (item: T) => EntityFieldConfig;
  onItemClick?: (item: T) => void;
  selectedItemId?: string;
  getId: (item: T) => string;
  compact?: boolean;
  emptyMessage?: string;
  /** Max height before scrolling — defaults to '600px' to match Tabulator */
  maxHeight?: string;
  /** DRR-189: Render action slot per card (e.g., Complete button for tasks) */
  renderActionSlot?: (item: T) => React.ReactNode;
}

export function EntityCardGrid<T>({
  items,
  fieldAdapter,
  onItemClick,
  selectedItemId,
  getId,
  compact,
  emptyMessage = 'No items found',
  maxHeight = '600px',
  renderActionSlot,
}: EntityCardGridProps<T>) {
  if (items.length === 0) {
    return (
      <Center py="xl">
        <Text c="dimmed">{emptyMessage}</Text>
      </Center>
    );
  }

  return (
    <ScrollArea h={maxHeight} offsetScrollbars>
      <SimpleGrid cols={{ base: 1, xs: 2, sm: 2, md: 3, lg: 4 }} spacing="md">
        {items.map((item) => {
          const id = getId(item);
          return (
            <EntityCard
              key={id}
              fields={fieldAdapter(item)}
              isSelected={selectedItemId === id}
              onClick={onItemClick ? () => onItemClick(item) : undefined}
              compact={compact}
              actionSlot={renderActionSlot?.(item)}
            />
          );
        })}
      </SimpleGrid>
    </ScrollArea>
  );
}

// ============================================================================
// FIELD ADAPTERS — DRY adapters for different entity shapes
// ============================================================================

/**
 * Adapter for FERP core primitives (all 25 types).
 * Maps PrimitiveItem → EntityFieldConfig using primitive-metadata.
 *
 * DRR-288: Optional getLabel for terminology resolution.
 * Pass useTerminology().getLabel to resolve labels like 'Subject' → 'Resident'.
 *
 * DRR-117: Optional resolveRuleset for mandate name resolution.
 * Pass useResolveRulesetName() to resolve '4-1-2' → 'MN 144G.71 - Medication Management'.
 */
export function primitiveFieldAdapter(
  primitiveType: PrimitiveType,
  resolveActorName?: (id: string | null | undefined) => string,
  getLabel?: (key: string) => string,
  resolveRuleset?: (id: string | null | undefined) => string,
): (item: PrimitiveItem) => EntityFieldConfig {
  const metadata = getPrimitiveMetadata(primitiveType);
  const Icon = metadata.icon as React.ComponentType<{ size: number }>;
  const resolve = resolveActorName ?? ((id: string | null | undefined) => String(id ?? '—'));
  const t = getLabel ?? ((key: string) => key); // Fallback to key if no terminology
  const resolveMandate = resolveRuleset ?? ((id: string | null | undefined) => String(id ?? '—'));

  return (item: PrimitiveItem): EntityFieldConfig => {
    const metaFields: Array<{ label: string; value: string }> = [];

    // Add type-specific key fields
    if (item.actorType) metaFields.push({ label: t('Type'), value: String(item.actorType) });
    if (item.policyType) metaFields.push({ label: t('Type'), value: String(item.policyType) });
    if (item.taskType) metaFields.push({ label: t('Type'), value: String(item.taskType) });
    if (item.priority) metaFields.push({ label: t('Priority'), value: String(item.priority) });
    // DRR-130: Task creation pathway — distinguish regulatory vs ad-hoc tasks
    if (item.createdBySource) {
      const sourceLabels: Record<string, string> = {
        RULE_ENGINE: t('Regulatory'),
        WORKFLOW_OPERATION: t('Workflow'),
        TENANT_DECISION: t('Ad-hoc'),
      };
      metaFields.push({ label: t('Source'), value: sourceLabels[String(item.createdBySource)] ?? String(item.createdBySource) });
    }
    // DRR-117: Mandate reference — regulatory bind for compliance tasks (resolved to name)
    if (item.mandateRef) metaFields.push({ label: t('Mandate'), value: resolveMandate(String(item.mandateRef)) });
    // T1: progressPercent — task completion (Gate §45)
    if (typeof item.progressPercent === 'number' && item.progressPercent > 0) {
      metaFields.push({ label: t('Progress'), value: `${item.progressPercent}%` });
    }
    // T1: errorMessage — task failure reason (Gate §45)
    if (item.errorMessage) {
      metaFields.push({ label: t('Error'), value: String(item.errorMessage).slice(0, 50) });
    }
    // T1: slaDeadline — regulatory deadline (Gate §45)
    if (item.slaDeadline || item.dueAt) {
      const deadline = item.slaDeadline ?? item.dueAt;
      const dueDate = new Date(String(deadline));
      const now = new Date();
      const isOverdue = dueDate < now;
      const dueLabel = isOverdue ? t('OVERDUE') : t('Due');
      metaFields.push({ label: dueLabel, value: dueDate.toLocaleDateString() });
    }
    // Generic config reference fallback for non-task primitives (resolved to name)
    const refField = !item.mandateRef ? (item.policyRef ?? item.rulesetRef) : null;
    if (refField) metaFields.push({ label: t('Reference'), value: resolveMandate(String(refField)) });
    if (item.subjectActorId) metaFields.push({ label: t('Subject'), value: resolve(String(item.subjectActorId)) });
    const assignedId = item.assignedActor?.id ?? item.assignedActorId;
    if (assignedId) metaFields.push({ label: t('Assigned'), value: resolve(item.assignedActor?.displayName ?? String(assignedId)) });
    if (item.createdBy) metaFields.push({ label: t('By'), value: resolve(String(item.createdBy)) });

    return {
      name: (item.name as string) ?? 'Unnamed',
      subtitle: (item.description as string) ?? undefined,
      status: item.status as string | undefined,
      safetyLevel: item.safetyLevel as string | undefined,
      imageUrl: (item.profileImageUrl as string) ?? undefined,
      metaFields: metaFields.slice(0, 4),
      accentColor: metadata.phaseColor,
      Icon,
    };
  };
}

/**
 * Adapter factory for subgraph entities (FHIR patients, ERP entities, etc.).
 * Uses SubgraphEntityType metadata to extract the right fields from each row.
 */
export function subgraphFieldAdapter(
  entityType: SubgraphEntityType,
): (item: Record<string, unknown>) => EntityFieldConfig {
  return (item: Record<string, unknown>): EntityFieldConfig => {
    const displayValue = item[entityType.displayField];
    const name = displayValue ? String(displayValue) : 'Unnamed';

    const metaFields: Array<{ label: string; value: string }> = [];
    for (const field of entityType.identifierFields.slice(0, 3)) {
      const val = item[field];
      if (val != null) {
        metaFields.push({
          label: field.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim(),
          value: String(val),
        });
      }
    }

    return {
      name,
      subtitle: entityType.description,
      status: item.status as string | undefined,
      safetyLevel: entityType.isLifeCritical ? 'ELEVATED' : undefined,
      metaFields,
      accentColor: entityType.isLifeCritical ? 'red' : 'blue',
    };
  };
}
